import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { JobsDeploymentService } from '../../services';
import { ConfirmationService, MessageService } from 'primeng';
import { Message } from '../../classes/Message';
import { AllocateJobDTO } from '../../interfaces/allocate-job';
import { TaskSupplierScheduleSource } from '../task-supplier-schedule/task-supplier-schedule-store';
import {
  ConfirmAllocationRequest,
  UpdateTaskScheduleDTO,
} from '../task-supplier-schedule/task-supplier-schedule';
import { OrganisationTypeEnum } from '../../enums/organisation-types';
import { ScheduleUnavailabilitySummaryDTO } from '../../interfaces/supplier-availability/schedule-unavailability-summary.dto.interface';
import { IDeploySupplierContext } from '../../interfaces/deploy-supplier-context';

interface JobAllocationInfo {
  id: number;
  price: number;
}

@Component({
  selector: 'allocate-job-dialog',
  templateUrl: './allocate-job-dialog.component.html',
  styleUrls: ['./allocate-job-dialog.component.scss'],
})
export class AllocateJobDialogComponent implements OnInit {
  get visible(): boolean {
    return this._visible;
  }

  @Input() set visible(value: boolean) {
    this._visible = value;
  }

  @Input() job: JobAllocationInfo;

  @Input() supplierId: number;

  @Input() referrPage: string;

  private _visible: boolean = false;

  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() supplierPlanClick: EventEmitter<null> = new EventEmitter<null>();

  @Output() jobAllocated: EventEmitter<null> = new EventEmitter<null>();

  public busy: boolean = false;

  public price: number;

  public activeIndex: number = 0;

  public taskScheduleSource: TaskSupplierScheduleSource = TaskSupplierScheduleSource.JobDeployment;

  public deploySupplierContext: IDeploySupplierContext;

  taskScheduleChanges: UpdateTaskScheduleDTO[];

  loading: boolean = false;

  constructor(
    private jobDeploymentService: JobsDeploymentService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit() {}

  close() {
    this.visibleChange.emit(false);
  }

  async onShow() {
    this.price = this.job.price;

    if (this.supplierId && this.taskScheduleSource == TaskSupplierScheduleSource.JobDeployment) {
      this.deploySupplierContext = await this.jobDeploymentService.getDeploySupplierContext(
        this.job.id,
        this.supplierId,
      );
    }
  }

  onSupplierPlanClick() {
    this.supplierPlanClick.emit();
  }

  async allocateJobToSupplier(request: ConfirmAllocationRequest) {
    // check supplier capability model
    let check = await this.jobDeploymentService.checkSupplierCapability(
      this.supplierId,
      this.job.id,
    );

    if (!check) {
      this.messageService.add(
        Message.Error(
          'This supplier does not have the capability to complete this job, please check service agreements.',
        ),
      );
      return;
    }

    if (this.deploySupplierContext && this.deploySupplierContext.isExcluded) {
      this.messageService.add(
        Message.Error('This supplier is excluded from working for a customer providing this job'),
      );
      return;
    }

    if (this.deploySupplierContext && !this.deploySupplierContext.tagRequirementsMet) {
      this.confirmationService.confirm({
        key: 'allocate-job-to-supplier',
        message:
          'This supplier does not match the tagging requirements for this job, are you sure you want to allocate?',
        header: 'Confirm Job Allocation',
        accept: () => {
          this.allocateJob(request);
        },
      });
    } else {
      this.allocateJob(request);
    }
  }

  allocateJob(confirmAllocationRequest: ConfirmAllocationRequest) {
    let request: AllocateJobDTO = {
      jobId: this.job.id,
      supplierId: this.supplierId,
      price: this.price,
      taskSupplierSchedules: confirmAllocationRequest.updateTaskScheduleDTOs,
      priceChangeReason: confirmAllocationRequest.priceChangeReason,
    };

    this.busy = true;

    this.jobDeploymentService
      .allocateJob(request)
      .then(() => {
        this.busy = false;
        this.jobAllocated.emit();
        this.visibleChange.emit(false);
      })
      .catch((err) => {
        this.busy = false;
        if (err.error && err.error.Message) {
          this.messageService.add(Message.Error(`${err.error.Message}`, 'Allocate Job'));
        } else {
          this.messageService.add(
            Message.Error(
              `An error occurred when trying to allocate job to Supplier`,
              'Allocate Job',
            ),
          );
        }
      });
  }

  protected readonly OrganisationTypeEnum = OrganisationTypeEnum;

  scheduleChanged(changes: UpdateTaskScheduleDTO[]) {
    this.taskScheduleChanges = changes;
  }
}
